import type { CarePlan, Goal, ServiceRequest } from '@medplum/fhirtypes';

import {
  type FhirConnectionState,
  type FhirQueryState,
  useFhirConnection,
  useFhirData,
} from '@eluve/fhir-data';
import { flattenFhirGraphQLData } from '@eluve/fhir-renderer';
import { type ResultOf } from '@eluve/graphql-fhir';

import {
  FHIR_PATIENT_ID_QUERY,
  PATIENT_CARE_PLANS_QUERY,
  PLAN_GOALS_QUERY,
  PLAN_SERVICE_REQUESTS_QUERY,
} from './fhirQueries';

/**
 * Data layer for the patient-profile Care Plans section: read-only FHIR
 * GraphQL connection reads (this surface performs no writes — plan
 * creation/activation belongs to the care-plan stack). Kept decoupled from
 * presentation per ENG-5369: the hooks return raw resources; every product
 * judgment lives in the pure `plan-list` derivations.
 */

function mapPatientId(
  data: ResultOf<typeof FHIR_PATIENT_ID_QUERY>,
): string | null {
  return data.PatientList?.[0]?.id ?? null;
}

/** Resolve the FHIR Patient id for an Eluve (Hasura) patient id. */
export function useFhirPatientId(
  patientId: string,
): FhirQueryState<string | null> {
  return useFhirData(FHIR_PATIENT_ID_QUERY, mapPatientId, {
    variables: { identifier: patientId },
  });
}

function mapCarePlans(
  data: ResultOf<typeof PATIENT_CARE_PLANS_QUERY>,
): CarePlan[] {
  return flattenFhirGraphQLData(data).filter(
    (resource): resource is CarePlan & { id: string } =>
      resource.resourceType === 'CarePlan' && Boolean(resource.id),
  );
}

/** The patient's care plans, paged. Skipped until the FHIR id resolves. */
export function usePatientCarePlans(
  fhirPatientId: string | null,
): FhirConnectionState<CarePlan[]> {
  return useFhirConnection(
    PATIENT_CARE_PLANS_QUERY,
    'CarePlanConnection',
    mapCarePlans,
    {
      variables: { patientRef: `Patient/${fhirPatientId}` },
      skip: !fhirPatientId,
    },
  );
}

function mapGoals(data: ResultOf<typeof PLAN_GOALS_QUERY>): Goal[] {
  return flattenFhirGraphQLData(data).filter(
    (resource): resource is Goal & { id: string } =>
      resource.resourceType === 'Goal' && Boolean(resource.id),
  );
}

/**
 * The goal window plan goal references resolve against. Loaded alongside
 * the plans; a miss just means a display-only goal row.
 */
export function usePlanGoals(
  fhirPatientId: string | null,
): FhirConnectionState<Goal[]> {
  return useFhirConnection(PLAN_GOALS_QUERY, 'GoalConnection', mapGoals, {
    variables: { patientRef: `Patient/${fhirPatientId}` },
    skip: !fhirPatientId,
  });
}

function mapServiceRequests(
  data: ResultOf<typeof PLAN_SERVICE_REQUESTS_QUERY>,
): ServiceRequest[] {
  return flattenFhirGraphQLData(data).filter(
    (resource): resource is ServiceRequest & { id: string } =>
      resource.resourceType === 'ServiceRequest' && Boolean(resource.id),
  );
}

/**
 * The ServiceRequest window plans resolve phase progress and activities
 * from (spine sticks + prescribed leaves, matched per plan via `basedOn`).
 * A plan whose requests fall outside the loaded window degrades to its
 * CarePlan-only summary.
 */
export function usePlanServiceRequests(
  fhirPatientId: string | null,
): FhirConnectionState<ServiceRequest[]> {
  return useFhirConnection(
    PLAN_SERVICE_REQUESTS_QUERY,
    'ServiceRequestConnection',
    mapServiceRequests,
    {
      variables: { patientRef: `Patient/${fhirPatientId}` },
      skip: !fhirPatientId,
    },
  );
}
